import React, { useState, useEffect, useMemo } from 'react';
import {
  Send,
  X,
  Check,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Plane,
  Wrench,
  Truck,
  ShieldCheck,
  Feather,
  Clock,
  Users,
  Building,
  Lock,
  Heart,
  Moon,
  UploadCloud,
  FileText,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { ingestionService } from '../modules/ingestion/ingestionService';
import { IngestionReportCategory } from '../modules/ingestion/types';
import { useEscapeKey } from '../shared/useGlobalModalEscape';

export interface SubmitReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateToIngestion?: () => void;
  initialKindId?: string;
  initialCategory?: IngestionReportCategory;
  onReportSubmitted?: () => void;
}

type StepNumber = 1 | 2 | 3 | 4;

export interface ReportKindOption {
  id: string;
  title: string;
  icon: React.ReactNode;
  category: IngestionReportCategory;
  reportType: string;
  description: string;
}

export const REPORT_KINDS: ReportKindOption[] = [
  {
    id: 'PILOT',
    title: 'Pilot Safety Report',
    icon: <Plane size={16} />,
    category: 'SAFETY',
    reportType: 'AIR_OPS',
    description: 'In-flight occurrences, cockpit observations, turbulence, approach and altitude anomalies'
  },
  {
    id: 'MAINTENANCE',
    title: 'Engineering and Maintenance Occurrence...',
    icon: <Wrench size={16} />,
    category: 'SAFETY',
    reportType: 'MAINTENANCE',
    description: 'Aircraft technical defects, MEL dispatch deviations, system warnings and component failures'
  },
  {
    id: 'GROUND_OPS',
    title: 'Ground and Cargo Operations Occurrence...',
    icon: <Truck size={16} />,
    category: 'SAFETY',
    reportType: 'GROUND_OPS',
    description: 'Ramp safety, baggage belt operations, pushback incidents, ground equipment scrapes'
  },
  {
    id: 'SECURITY',
    title: 'Security Report',
    icon: <ShieldCheck size={16} />,
    category: 'SAFETY',
    reportType: 'SECURITY',
    description: 'Security breaches, unverified personnel access, baggage security, unruly passenger events'
  },
  {
    id: 'BIRD_STRIKE',
    title: 'Bird Strike Report',
    icon: <Feather size={16} />,
    category: 'SAFETY',
    reportType: 'WILDLIFE_BIRD_STRIKE',
    description: 'Wildlife and bird impact during takeoff, initial climb, cruise, flare, or landing roll'
  },
  {
    id: 'FDP_EXTENSION',
    title: 'Extension of Flight Duty Period',
    icon: <Clock size={16} />,
    category: 'SAFETY',
    reportType: 'FDP_EXTENSION',
    description: 'Commander discretion exercised for flight duty period extension under EASA ORO.FTL'
  },
  {
    id: 'CABIN_SAFETY',
    title: 'Cabin Safety Report',
    icon: <Users size={16} />,
    category: 'SAFETY',
    reportType: 'CABIN_SAFETY',
    description: 'Passenger or cabin crew injury, cabin smoke/fumes, door slide anomalies, galley defects'
  },
  {
    id: 'FACILITIES',
    title: 'TS & Facilities Report',
    icon: <Building size={16} />,
    category: 'NON_SAFETY',
    reportType: 'TECHNICAL_FACILITIES',
    description: 'Hangar, technical store, office, ramp floodlight, or IT infrastructure discrepancies'
  },
  {
    id: 'FATIGUE',
    title: 'Fatigue Report',
    icon: <Moon size={16} />,
    category: 'SAFETY',
    reportType: 'FATIGUE',
    description: 'Fatigue risk management report, cumulative sleep debt or circadian disruption'
  }
];

export const SubmitReportModal: React.FC<SubmitReportModalProps> = ({
  isOpen,
  onClose,
  onNavigateToIngestion,
  initialKindId,
  initialCategory,
  onReportSubmitted,
}) => {
  // Stepper State (Steps 1 to 4)
  const [currentStep, setCurrentStep] = useState<StepNumber>(1);

  // Step 1: Kind of Report
  const [selectedKindId, setSelectedKindId] = useState<string>(() => {
    if (initialKindId) return initialKindId;
    if (initialCategory === 'NON_SAFETY') return 'FACILITIES';
    return 'PILOT';
  });

  // Reset or initialize state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setSubmittedResult(null);
      if (initialKindId) {
        setSelectedKindId(initialKindId);
      } else if (initialCategory === 'NON_SAFETY') {
        setSelectedKindId('FACILITIES');
      } else {
        setSelectedKindId('PILOT');
      }
    }
  }, [isOpen, initialKindId, initialCategory]);

  // Step 2: General Information
  const [subjectTitle, setSubjectTitle] = useState<string>('');
  const [eventDate, setEventDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [eventTime, setEventTime] = useState<string>('12:00');
  const [flightNumber, setFlightNumber] = useState<string>('');
  const [aircraftReg, setAircraftReg] = useState<string>('');
  const [airportLocation, setAirportLocation] = useState<string>('LTFM (Istanbul Airport)');
  const [department, setDepartment] = useState<string>('Flight Operations');
  const [severity, setSeverity] = useState<string>('MEDIUM');
  const [isConfidential, setIsConfidential] = useState<boolean>(false);
  const [identityMode, setIdentityMode] = useState<'IDENTIFIED' | 'CONFIDENTIAL' | 'ANONYMOUS'>('IDENTIFIED');
  const [isMor, setIsMor] = useState<boolean>(false);

  // Step 3: Occurrence Information (Screenshots 4 & 5)
  const [whatHappened, setWhatHappened] = useState<string>('');
  const [whyHappened, setWhyHappened] = useState<string>('');
  const [correctiveAction, setCorrectiveAction] = useState<string>('');

  // Step 2: Dynamic fields based on Report Type ("Burcu'dan gelen form" mimarisi)
  // Flight / ASR
  const [departureAirport, setDepartureAirport] = useState<string>('LTFM (Istanbul Airport)');
  const [destinationAirport, setDestinationAirport] = useState<string>('');
  const [flightPhase, setFlightPhase] = useState<string>('Cruise');
  const [altitudeFlightLevel, setAltitudeFlightLevel] = useState<string>('');
  const [weatherConditions, setWeatherConditions] = useState<string>('CAVOK');

  // Cabin Safety
  const [cabinArea, setCabinArea] = useState<string>('Forward Galley');
  const [seatNumber, setSeatNumber] = useState<string>('');
  const [personsAffected, setPersonsAffected] = useState<string>('None');
  const [cabinEquipmentInvolved, setCabinEquipmentInvolved] = useState<string>('');

  // Maintenance & Engineering
  const [maintenanceStation, setMaintenanceStation] = useState<string>('IST Line Hangar');
  const [ataChapter, setAtaChapter] = useState<string>('ATA 29 - Hydraulic Power');
  const [partName, setPartName] = useState<string>('');
  const [partNumber, setPartNumber] = useState<string>('');
  const [serialNumber, setSerialNumber] = useState<string>('');
  const [techLogRef, setTechLogRef] = useState<string>('');
  const [melItemRef, setMelItemRef] = useState<string>('');

  // Ground Operations
  const [apronGateStand, setApronGateStand] = useState<string>('');
  const [groundHandlingAgent, setGroundHandlingAgent] = useState<string>('TGS');
  const [groundEquipmentType, setGroundEquipmentType] = useState<string>('Pushback Tug');

  // Security
  const [securityDomain, setSecurityDomain] = useState<string>('Airport Restricted Area / Access Control');
  const [authoritiesNotified, setAuthoritiesNotified] = useState<string>('Not Required');

  // Bird Strike
  const [runwayUsed, setRunwayUsed] = useState<string>('');
  const [partStruck, setPartStruck] = useState<string>('Radome / Nose');
  const [birdSpeciesSize, setBirdSpeciesSize] = useState<string>('Medium (Gull / Pigeon)');
  const [birdCount, setBirdCount] = useState<string>('Single (1)');
  const [pilotWarned, setPilotWarned] = useState<string>('No');

  // Fatigue
  const [dutyStartTime, setDutyStartTime] = useState<string>('06:00');
  const [dutyEndTime, setDutyEndTime] = useState<string>('18:00');
  const [consecutiveDutyDays, setConsecutiveDutyDays] = useState<string>('3');
  const [sleepLast24, setSleepLast24] = useState<string>('6.0');
  const [sleepLast48, setSleepLast48] = useState<string>('12.0');
  const [fatigueRating, setFatigueRating] = useState<string>('4 - A little tired, less than fresh');

  // FDP Extension
  const [scheduledDutyEnd, setScheduledDutyEnd] = useState<string>('17:00');
  const [actualDutyEnd, setActualDutyEnd] = useState<string>('18:30');
  const [extensionMinutes, setExtensionMinutes] = useState<string>('90');
  const [discretionReason, setDiscretionReason] = useState<string>('Severe Weather & Slot Delay');
  const [restFacilityUsed, setRestFacilityUsed] = useState<string>('Class 2 (Lie-flat seat)');

  // TS & Facilities
  const [facilityName, setFacilityName] = useState<string>('Main Technical Hangar');
  const [roomBayRef, setRoomBayRef] = useState<string>('Bay 2');
  const [facilityDiscrepancyType, setFacilityDiscrepancyType] = useState<string>('Equipment / Tooling Calibration');

  // Auto-align default department when report kind changes
  useEffect(() => {
    switch (selectedKindId) {
      case 'PILOT':
      case 'BIRD_STRIKE':
      case 'FDP_EXTENSION':
      case 'FATIGUE':
        setDepartment('Flight Operations');
        break;
      case 'CABIN_SAFETY':
        setDepartment('Cabin Safety');
        break;
      case 'MAINTENANCE':
        setDepartment('Maintenance / Engineering');
        break;
      case 'GROUND_OPS':
        setDepartment('Ground Operations');
        break;
      case 'SECURITY':
        setDepartment('Security');
        break;
      case 'FACILITIES':
        setDepartment('Quality Assurance');
        break;
    }
  }, [selectedKindId]);

  // Step 4: Attachments
  const [attachments, setAttachments] = useState<Array<{ name: string; size: string }>>([]);
  const [reporterNotes, setReporterNotes] = useState<string>('');

  // Submission State
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submittedResult, setSubmittedResult] = useState<{
    referenceNumber: string;
    reportTitle: string;
  } | null>(null);


  const handleResetAndClose = () => {
    setCurrentStep(1);
    setSelectedKindId('PILOT');
    setSubjectTitle('');
    setWhatHappened('');
    setWhyHappened('');
    setCorrectiveAction('');
    setAttachments([]);
    setReporterNotes('');
    setSubmittedResult(null);
    onClose();
  };

  useEscapeKey(handleResetAndClose, isOpen);

  if (!isOpen) return null;

  const selectedReportKind = REPORT_KINDS.find((k) => k.id === selectedKindId) || REPORT_KINDS[0];

  const handleNextFromStep1 = () => {
    setCurrentStep(2);
  };

  const handleNextFromStep2 = () => {
    if (!subjectTitle.trim()) {
      alert('Please enter a Subject / Event Title before proceeding.');
      return;
    }
    setCurrentStep(3);
  };

  const handleNextFromStep3 = () => {
    setCurrentStep(4);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArr = Array.from(e.target.files);
      const newItems = filesArr.map((f) => ({
        name: f.name,
        size: `${(f.size / 1024).toFixed(1)} KB`
      }));
      setAttachments((prev) => [...prev, ...newItems]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFinalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const generatedRef = `REP-2026-${Math.floor(1000 + Math.random() * 9000)}`;

    try {
      const specificDetails: Record<string, any> = {};
      if (selectedReportKind.id === 'PILOT') {
        specificDetails.flight_phase = flightPhase;
        specificDetails.departure_airport = departureAirport;
        specificDetails.destination_airport = destinationAirport;
        specificDetails.altitude_flight_level = altitudeFlightLevel;
        specificDetails.weather_conditions = weatherConditions;
      } else if (selectedReportKind.id === 'CABIN_SAFETY') {
        specificDetails.flight_phase = flightPhase;
        specificDetails.cabin_area = cabinArea;
        specificDetails.seat_number = seatNumber;
        specificDetails.persons_affected = personsAffected;
        specificDetails.cabin_equipment_involved = cabinEquipmentInvolved;
      } else if (selectedReportKind.id === 'MAINTENANCE') {
        specificDetails.maintenance_station = maintenanceStation;
        specificDetails.ata_chapter = ataChapter;
        specificDetails.part_name = partName;
        specificDetails.part_number = partNumber;
        specificDetails.serial_number = serialNumber;
        specificDetails.tech_log_ref = techLogRef;
        specificDetails.mel_item_ref = melItemRef;
      } else if (selectedReportKind.id === 'GROUND_OPS') {
        specificDetails.apron_gate_stand = apronGateStand;
        specificDetails.ground_handling_agent = groundHandlingAgent;
        specificDetails.ground_equipment_type = groundEquipmentType;
      } else if (selectedReportKind.id === 'SECURITY') {
        specificDetails.security_domain = securityDomain;
        specificDetails.authorities_notified = authoritiesNotified;
      } else if (selectedReportKind.id === 'BIRD_STRIKE') {
        specificDetails.runway_used = runwayUsed;
        specificDetails.flight_phase = flightPhase;
        specificDetails.part_struck = partStruck;
        specificDetails.bird_species_size = birdSpeciesSize;
        specificDetails.bird_count = birdCount;
        specificDetails.pilot_warned = pilotWarned;
      } else if (selectedReportKind.id === 'FATIGUE') {
        specificDetails.duty_start_time = dutyStartTime;
        specificDetails.duty_end_time = dutyEndTime;
        specificDetails.consecutive_duty_days = consecutiveDutyDays;
        specificDetails.sleep_last_24 = sleepLast24;
        specificDetails.sleep_last_48 = sleepLast48;
        specificDetails.fatigue_rating = fatigueRating;
      } else if (selectedReportKind.id === 'FDP_EXTENSION') {
        specificDetails.scheduled_duty_end = scheduledDutyEnd;
        specificDetails.actual_duty_end = actualDutyEnd;
        specificDetails.extension_minutes = extensionMinutes;
        specificDetails.discretion_reason = discretionReason;
        specificDetails.rest_facility_used = restFacilityUsed;
      } else if (selectedReportKind.id === 'FACILITIES') {
        specificDetails.facility_name = facilityName;
        specificDetails.room_bay_ref = roomBayRef;
        specificDetails.facility_discrepancy_type = facilityDiscrepancyType;
      }

      let activeUser: any = null;
      try {
        const userStr = localStorage.getItem('auth_user');
        if (userStr) activeUser = JSON.parse(userStr);
      } catch {}

      const isAnon = identityMode === 'ANONYMOUS';
      const reporterName = isAnon ? undefined : (activeUser?.display_name || activeUser?.name || activeUser?.username || 'Authenticated Personnel');
      const reporterEmail = isAnon ? undefined : (activeUser?.email || activeUser?.primary_email || undefined);
      const createdBy = isAnon ? undefined : (activeUser?.id || activeUser?.username || undefined);

      const payload = {
        report_category: selectedReportKind.category,
        report_type: selectedReportKind.reportType,
        title: subjectTitle.trim() || selectedReportKind.title,
        description: `What Happened:\n${whatHappened || 'Not specified'}\n\nWhy Happened:\n${whyHappened || 'Not specified'}\n\nCorrective Action & Related Procedure:\n${correctiveAction || 'None specified'}\n\nAdditional Remarks:\n${reporterNotes || 'None'}`,
        event_date: `${eventDate}T${eventTime}:00Z`,
        event_location: airportLocation,
        airport_code: airportLocation.substring(0, 4),
        flight_number: flightNumber.trim() || undefined,
        aircraft_registration: aircraftReg.trim() || undefined,
        department_id: department,
        initial_severity: severity,
        is_mandatory_occurrence: false,
        confidentiality_level: isConfidential || identityMode === 'CONFIDENTIAL' ? 'CONFIDENTIAL' : identityMode === 'ANONYMOUS' ? 'STRICTLY_CONFIDENTIAL' : 'RESTRICTED',
        identity_mode: identityMode === 'ANONYMOUS' ? 'ANONYMOUS' : (isConfidential || identityMode === 'CONFIDENTIAL' ? 'DE_IDENTIFIED' : 'NAMED'),
        reporter_name: reporterName,
        reporter_email: reporterEmail,
        created_by: createdBy,
        context_data: {
          report_kind_id: selectedReportKind.id,
          report_kind_title: selectedReportKind.title,
          what_happened: whatHappened,
          why_happened: whyHappened,
          corrective_action_and_procedure: correctiveAction,
          attached_files: attachments.map((a) => a.name),
          ...specificDetails,
        }
      };

      const res = await ingestionService.createReport(payload).catch(() => null);
      const finalRef = res?.report?.report_number || generatedRef;

      setSubmittedResult({
        referenceNumber: finalRef,
        reportTitle: subjectTitle.trim() || selectedReportKind.title
      });
      if (onReportSubmitted) {
        onReportSubmitted();
      }
    } catch {
      setSubmittedResult({
        referenceNumber: generatedRef,
        reportTitle: subjectTitle.trim() || selectedReportKind.title
      });
      if (onReportSubmitted) {
        onReportSubmitted();
      }
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="submit-modal-backdrop" onClick={handleResetAndClose}>
      <div
        className="submit-modal-card submit-modal-wide"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '840px', width: '92%', maxHeight: '90vh' }}
      >
        {/* Modal Header */}
        <div className="submit-modal-header" style={{ marginBottom: '14px', paddingBottom: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '12px',
                backgroundColor: '#0f172a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                boxShadow: '0 2px 6px rgba(15, 23, 42, 0.2)'
              }}
            >
              <Send size={19} color="#ffffff" style={{ transform: 'rotate(0deg)' }} />
            </div>
            <div>
              <h3 className="submit-modal-title" style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>
                Submit Safety Report
              </h3>
              <p className="submit-modal-sub" style={{ fontSize: '12.5px', color: '#64748b', marginTop: '2px' }}>
                Log a new occurrence, hazard, or safety observation
              </p>
            </div>
          </div>
          <button className="submit-modal-close" onClick={handleResetAndClose} title="Close">
            <X size={19} />
          </button>
        </div>

        {/* 4-Step Stepper Progress Bar */}
        <div className="submit-stepper-bar">
          {/* Step 1 */}
          <div
            className={`submit-stepper-step ${currentStep === 1 ? 'active' : currentStep > 1 ? 'completed' : ''}`}
            onClick={() => currentStep > 1 && setCurrentStep(1)}
            style={{ cursor: currentStep > 1 ? 'pointer' : 'default' }}
          >
            <div className="submit-stepper-circle">
              {currentStep > 1 ? <Check size={12} strokeWidth={3} /> : '1'}
            </div>
            <span className="submit-stepper-label">Kind of Report</span>
          </div>

          <div className={`submit-stepper-line ${currentStep > 1 ? 'completed' : ''}`} />

          {/* Step 2 */}
          <div
            className={`submit-stepper-step ${currentStep === 2 ? 'active' : currentStep > 2 ? 'completed' : ''}`}
            onClick={() => currentStep > 2 && setCurrentStep(2)}
            style={{ cursor: currentStep > 2 ? 'pointer' : 'default' }}
          >
            <div className="submit-stepper-circle">
              {currentStep > 2 ? <Check size={12} strokeWidth={3} /> : '2'}
            </div>
            <span className="submit-stepper-label">General Information</span>
          </div>

          <div className={`submit-stepper-line ${currentStep > 2 ? 'completed' : ''}`} />

          {/* Step 3 */}
          <div
            className={`submit-stepper-step ${currentStep === 3 ? 'active' : currentStep > 3 ? 'completed' : ''}`}
            onClick={() => currentStep > 3 && setCurrentStep(3)}
            style={{ cursor: currentStep > 3 ? 'pointer' : 'default' }}
          >
            <div className="submit-stepper-circle">
              {currentStep > 3 ? <Check size={12} strokeWidth={3} /> : '3'}
            </div>
            <span className="submit-stepper-label">Occurrence Information</span>
          </div>

          <div className={`submit-stepper-line ${currentStep > 3 ? 'completed' : ''}`} />

          {/* Step 4 */}
          <div
            className={`submit-stepper-step ${currentStep === 4 ? 'active' : ''}`}
          >
            <div className="submit-stepper-circle">
              {submittedResult ? <Check size={12} strokeWidth={3} /> : '4'}
            </div>
            <span className="submit-stepper-label">Attachment</span>
          </div>
        </div>

        {/* Modal Main Body Content */}
        {submittedResult ? (
          <div className="submit-success-box" style={{ padding: '36px 20px', textAlign: 'center' }}>
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: '#ecfdf5',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px'
              }}
            >
              <CheckCircle2 size={38} color="#10b981" />
            </div>
            <h4 style={{ fontSize: '19px', fontWeight: 700, color: '#0f172a', margin: '0 0 6px 0' }}>
              Safety Report Submitted Successfully
            </h4>
            <div style={{ margin: '12px 0 16px 0', display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
              <span
                style={{
                  fontFamily: 'ui-monospace, monospace',
                  fontSize: '15px',
                  fontWeight: 700,
                  backgroundColor: '#eff6ff',
                  color: '#1d4ed8',
                  padding: '4px 14px',
                  borderRadius: '6px',
                  border: '1px solid #bfdbfe'
                }}
              >
                {submittedResult.referenceNumber}
              </span>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  backgroundColor: '#fef3c7',
                  color: '#b45309',
                  border: '1px solid #fde68a',
                  borderRadius: '9999px',
                  padding: '4px 12px',
                  fontSize: '12px',
                  fontWeight: 700
                }}
              >
                ⏳ Waiting Assessment
              </span>
            </div>
            <p style={{ fontSize: '13.5px', color: '#475569', maxWidth: '540px', margin: '0 auto 24px auto', lineHeight: 1.5 }}>
              Your report <b>"{submittedResult.reportTitle}"</b> has been logged with status <b>Waiting Assessment</b>. An email notification has been dispatched to you and the SMS assessment desk. You can edit this report at any time until SMS personnel begin assessment.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                type="button"
                className="submit-btn-back"
                onClick={handleResetAndClose}
              >
                Close Window
              </button>
              {onNavigateToIngestion && (
                <button
                  type="button"
                  className="submit-btn-next"
                  onClick={() => {
                    handleResetAndClose();
                    onNavigateToIngestion();
                  }}
                >
                  <span>View in Ingestion Module</span>
                  <ArrowRight size={14} />
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="submit-modal-content-area" style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            {/* STEP 1: KIND OF REPORT (Screenshots 1 & 2) */}
            {currentStep === 1 && (
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                {/* 1. Preliminary Confidentiality & Mandatory Occurrence (MOR) Selection */}
                <div
                  style={{
                    marginBottom: '16px',
                    padding: '12px 14px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  {/* Confidentiality Selection */}
                  <div>
                    <label style={{ fontSize: '11.5px', fontWeight: 800, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                      <Lock size={13} color="#2563eb" />
                      Report Confidentiality & Identity
                    </label>
                    <div style={{ display: 'flex', gap: '8px', maxWidth: '380px' }}>
                      <button
                        type="button"
                        onClick={() => { setIsConfidential(false); setIdentityMode('IDENTIFIED'); }}
                        style={{
                          flex: 1,
                          padding: '7px 10px',
                          fontSize: '12px',
                          fontWeight: !isConfidential && identityMode === 'IDENTIFIED' ? 700 : 500,
                          borderRadius: '6px',
                          border: !isConfidential && identityMode === 'IDENTIFIED' ? '2px solid #2563eb' : '1px solid #cbd5e1',
                          background: !isConfidential && identityMode === 'IDENTIFIED' ? '#eff6ff' : '#ffffff',
                          color: !isConfidential && identityMode === 'IDENTIFIED' ? '#1d4ed8' : '#475569',
                          cursor: 'pointer',
                        }}
                      >
                        Standard (Identified)
                      </button>
                      <button
                        type="button"
                        onClick={() => { setIsConfidential(true); setIdentityMode('CONFIDENTIAL'); }}
                        style={{
                          flex: 1,
                          padding: '7px 10px',
                          fontSize: '12px',
                          fontWeight: isConfidential || identityMode === 'CONFIDENTIAL' ? 700 : 500,
                          borderRadius: '6px',
                          border: isConfidential || identityMode === 'CONFIDENTIAL' ? '2px solid #059669' : '1px solid #cbd5e1',
                          background: isConfidential || identityMode === 'CONFIDENTIAL' ? '#f0fdf4' : '#ffffff',
                          color: isConfidential || identityMode === 'CONFIDENTIAL' ? '#047857' : '#475569',
                          cursor: 'pointer',
                        }}
                      >
                        🔒 Confidential
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px',
                    padding: '0 2px'
                  }}
                >
                  <span
                    style={{
                      fontSize: '11.5px',
                      fontWeight: 800,
                      letterSpacing: '0.06em',
                      color: '#475569',
                      textTransform: 'uppercase'
                    }}
                  >
                    What Kind of Report Do You Want to Submit?
                  </span>
                  <span style={{ fontSize: '11.5px', fontWeight: 600, color: '#64748b' }}>
                    Select one category ▲
                  </span>
                </div>

                {/* Cards Scrollable Grid */}
                <div className="submit-kind-scroll-wrap">
                  <div className="submit-kind-grid">
                    {REPORT_KINDS.map((kind) => {
                      const isSelected = selectedKindId === kind.id;
                      return (
                        <div
                          key={kind.id}
                          className={`submit-kind-card ${isSelected ? 'selected' : ''}`}
                          onClick={() => setSelectedKindId(kind.id)}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                            <div className={`submit-kind-icon ${isSelected ? 'selected' : 'unselected'}`}>
                              {kind.icon}
                            </div>
                            <div className={`submit-kind-radio ${isSelected ? 'selected' : ''}`}>
                              {isSelected && <div className="submit-kind-radio-inner" />}
                            </div>
                          </div>
                          <div className="submit-kind-title" title={kind.title}>
                            {kind.title}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Step 1 Actions */}
                <div className="submit-step-footer" style={{ justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="submit-btn-next"
                    onClick={handleNextFromStep1}
                  >
                    <span>Next</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2: GENERAL INFORMATION (Screenshot 3 + Existing Fields) */}
            {currentStep === 2 && (
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <div style={{ marginBottom: '14px' }}>
                  <h4 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', margin: '0 0 4px 0' }}>
                    General Information
                  </h4>
                  <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                    Selected Category: <b style={{ color: '#0f172a' }}>{selectedReportKind.title}</b> ({selectedReportKind.category})
                  </p>
                </div>

                <div className="submit-form-scroll-wrap">
                  <div className="submit-form-fields-grid">
                    {/* Row 1: Subject / Short Title */}
                    <div className="submit-field" style={{ gridColumn: '1 / -1' }}>
                      <label htmlFor="rep-subject">
                        Subject / Event Title <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input
                        id="rep-subject"
                        type="text"
                        placeholder="e.g. Unstabilized Approach on RWY 35L or Hydraulic Low Pressure"
                        value={subjectTitle}
                        onChange={(e) => setSubjectTitle(e.target.value)}
                        required
                        autoFocus
                      />
                    </div>

                    {/* Row 2: Date & Time */}
                    <div className="submit-field">
                      <label htmlFor="rep-date">
                        Occurrence Date <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input
                        id="rep-date"
                        type="date"
                        value={eventDate}
                        onChange={(e) => setEventDate(e.target.value)}
                        required
                      />
                    </div>

                    <div className="submit-field">
                      <label htmlFor="rep-time">
                        Time (UTC / Local) <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input
                        id="rep-time"
                        type="time"
                        value={eventTime}
                        onChange={(e) => setEventTime(e.target.value)}
                        required
                      />
                    </div>

                    {/* Common Row 3: Assigned Department & Severity */}
                    <div className="submit-field">
                      <label htmlFor="rep-dept">Assigned / Responsible Department</label>
                      <select
                        id="rep-dept"
                        value={department}
                        onChange={(e) => setDepartment(e.target.value)}
                        className="submit-select"
                      >
                        <option value="Flight Operations">Flight Operations</option>
                        <option value="Cabin Safety">Cabin Safety</option>
                        <option value="Ground Operations">Ground Operations</option>
                        <option value="Maintenance / Engineering">Maintenance / Engineering</option>
                        <option value="Airfield Safety">Airfield Safety</option>
                        <option value="Security">Security</option>
                        <option value="Quality Assurance">Quality Assurance</option>
                      </select>
                    </div>

                    <div className="submit-field">
                      <label htmlFor="rep-severity">Initial Severity Assessment</label>
                      <div className="submit-severity-selector" style={{ marginTop: '2px' }}>
                        {['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map((sev) => (
                          <button
                            key={sev}
                            type="button"
                            className={`submit-severity-pill sev-${sev.toLowerCase()} ${severity === sev ? 'active' : ''}`}
                            onClick={() => setSeverity(sev)}
                          >
                            {sev}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* DYNAMIC FORM SECTION: Tailored based on Report Type ("Burcu'dan gelen form") */}
                    <div style={{ gridColumn: '1 / -1', marginTop: '6px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#1e40af', background: '#eff6ff', padding: '3px 8px', borderRadius: '4px', border: '1px solid #bfdbfe' }}>
                          📋 {selectedReportKind.title} Specific Fields
                        </span>
                      </div>

                      {/* 1. PILOT (ASR) */}
                      {selectedReportKind.id === 'PILOT' && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                          <div className="submit-field">
                            <label>Flight Number</label>
                            <input
                              type="text"
                              placeholder="e.g. TK 1824"
                              value={flightNumber}
                              onChange={(e) => setFlightNumber(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Aircraft Registration / Fleet</label>
                            <input
                              type="text"
                              placeholder="e.g. TC-JHV (A321-200)"
                              value={aircraftReg}
                              onChange={(e) => setAircraftReg(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Departure Airport (ICAO/IATA)</label>
                            <input
                              type="text"
                              placeholder="e.g. LTFM (Istanbul)"
                              value={departureAirport}
                              onChange={(e) => setDepartureAirport(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Destination Airport (ICAO/IATA)</label>
                            <input
                              type="text"
                              placeholder="e.g. EGLL (London Heathrow)"
                              value={destinationAirport}
                              onChange={(e) => setDestinationAirport(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Flight Phase</label>
                            <select
                              value={flightPhase}
                              onChange={(e) => setFlightPhase(e.target.value)}
                              className="submit-select"
                            >
                              <option value="Pre-flight / Boarding">Pre-flight / Boarding</option>
                              <option value="Pushback / Taxi Out">Pushback / Taxi Out</option>
                              <option value="Takeoff Roll">Takeoff Roll</option>
                              <option value="Initial Climb (<10,000 ft)">Initial Climb (&lt;10,000 ft)</option>
                              <option value="Cruise">Cruise</option>
                              <option value="Descent">Descent</option>
                              <option value="Approach">Approach</option>
                              <option value="Go-Around / Missed Approach">Go-Around / Missed Approach</option>
                              <option value="Landing Roll">Landing Roll</option>
                              <option value="Taxi In / Parking">Taxi In / Parking</option>
                            </select>
                          </div>
                          <div className="submit-field">
                            <label>Altitude / Flight Level</label>
                            <input
                              type="text"
                              placeholder="e.g. FL340 or 2,500 ft AGL"
                              value={altitudeFlightLevel}
                              onChange={(e) => setAltitudeFlightLevel(e.target.value)}
                            />
                          </div>
                          <div className="submit-field" style={{ gridColumn: '1 / -1' }}>
                            <label>Weather / Environmental Conditions</label>
                            <input
                              type="text"
                              placeholder="e.g. CAVOK, Light Rain, Moderate Turbulence, Tailwind 12kt"
                              value={weatherConditions}
                              onChange={(e) => setWeatherConditions(e.target.value)}
                            />
                          </div>
                        </div>
                      )}

                      {/* 2. CABIN_SAFETY */}
                      {selectedReportKind.id === 'CABIN_SAFETY' && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                          <div className="submit-field">
                            <label>Flight Number</label>
                            <input
                              type="text"
                              placeholder="e.g. TK 1982"
                              value={flightNumber}
                              onChange={(e) => setFlightNumber(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Aircraft Registration</label>
                            <input
                              type="text"
                              placeholder="e.g. TC-LSD (A350-900)"
                              value={aircraftReg}
                              onChange={(e) => setAircraftReg(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Flight Phase</label>
                            <select
                              value={flightPhase}
                              onChange={(e) => setFlightPhase(e.target.value)}
                              className="submit-select"
                            >
                              <option value="Boarding">Boarding</option>
                              <option value="Taxi">Taxi</option>
                              <option value="Climb">Climb</option>
                              <option value="Cruise / Service">Cruise / Service</option>
                              <option value="Descent / Approach">Descent / Approach</option>
                              <option value="Landing">Landing</option>
                              <option value="Disembarking">Disembarking</option>
                            </select>
                          </div>
                          <div className="submit-field">
                            <label>Cabin Zone / Area Affected</label>
                            <select
                              value={cabinArea}
                              onChange={(e) => setCabinArea(e.target.value)}
                              className="submit-select"
                            >
                              <option value="Forward Galley">Forward Galley</option>
                              <option value="Mid Cabin Galley">Mid Cabin Galley</option>
                              <option value="Aft Galley">Aft Galley</option>
                              <option value="Business Cabin Seats">Business Cabin Seats</option>
                              <option value="Economy Cabin Seats">Economy Cabin Seats</option>
                              <option value="Lavatories">Lavatories</option>
                              <option value="Overhead Bins">Overhead Bins</option>
                              <option value="Cabin Door / Slide">Cabin Door / Slide</option>
                            </select>
                          </div>
                          <div className="submit-field">
                            <label>Seat Number(s) Involved</label>
                            <input
                              type="text"
                              placeholder="e.g. 14A, 22C (or N/A)"
                              value={seatNumber}
                              onChange={(e) => setSeatNumber(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Persons Affected</label>
                            <select
                              value={personsAffected}
                              onChange={(e) => setPersonsAffected(e.target.value)}
                              className="submit-select"
                            >
                              <option value="None">None</option>
                              <option value="Passenger">Passenger</option>
                              <option value="Cabin Crew">Cabin Crew</option>
                              <option value="Flight Crew">Flight Crew</option>
                              <option value="Infant / Child">Infant / Child</option>
                            </select>
                          </div>
                          <div className="submit-field" style={{ gridColumn: '1 / -1' }}>
                            <label>Cabin Equipment Involved</label>
                            <input
                              type="text"
                              placeholder="e.g. Meal Cart / Trolley, Coffee Maker, Oxygen Mask, Seatbelt, Emergency Slide"
                              value={cabinEquipmentInvolved}
                              onChange={(e) => setCabinEquipmentInvolved(e.target.value)}
                            />
                          </div>
                        </div>
                      )}

                      {/* 3. MAINTENANCE */}
                      {selectedReportKind.id === 'MAINTENANCE' && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                          <div className="submit-field">
                            <label>Aircraft Registration / Fleet</label>
                            <input
                              type="text"
                              placeholder="e.g. TC-JPP (A320neo)"
                              value={aircraftReg}
                              onChange={(e) => setAircraftReg(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Maintenance Base / Station</label>
                            <input
                              type="text"
                              placeholder="e.g. IST Line Hangar / SAW Base"
                              value={maintenanceStation}
                              onChange={(e) => setMaintenanceStation(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>ATA Chapter</label>
                            <select
                              value={ataChapter}
                              onChange={(e) => setAtaChapter(e.target.value)}
                              className="submit-select"
                            >
                              <option value="ATA 21 - Air Conditioning / Pressurization">ATA 21 - Air Conditioning / Pressurization</option>
                              <option value="ATA 24 - Electrical Power">ATA 24 - Electrical Power</option>
                              <option value="ATA 27 - Flight Controls">ATA 27 - Flight Controls</option>
                              <option value="ATA 28 - Fuel System">ATA 28 - Fuel System</option>
                              <option value="ATA 29 - Hydraulic Power">ATA 29 - Hydraulic Power</option>
                              <option value="ATA 30 - Ice and Rain Protection">ATA 30 - Ice and Rain Protection</option>
                              <option value="ATA 32 - Landing Gear & Brakes">ATA 32 - Landing Gear & Brakes</option>
                              <option value="ATA 33 - Lights">ATA 33 - Lights</option>
                              <option value="ATA 34 - Navigation / Avionics">ATA 34 - Navigation / Avionics</option>
                              <option value="ATA 35 - Oxygen System">ATA 35 - Oxygen System</option>
                              <option value="ATA 49 - Airborne Auxiliary Power (APU)">ATA 49 - Airborne Auxiliary Power (APU)</option>
                              <option value="ATA 52 - Doors">ATA 52 - Doors</option>
                              <option value="ATA 71-80 - Powerplant / Engine">ATA 71-80 - Powerplant / Engine</option>
                            </select>
                          </div>
                          <div className="submit-field">
                            <label>Part Name & Number (P/N)</label>
                            <input
                              type="text"
                              placeholder="e.g. Hydraulic Actuator P/N 45201-102"
                              value={partNumber}
                              onChange={(e) => setPartNumber(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Part Serial Number (S/N)</label>
                            <input
                              type="text"
                              placeholder="e.g. SN-89210-B"
                              value={serialNumber}
                              onChange={(e) => setSerialNumber(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Tech Log Book / Work Order Ref</label>
                            <input
                              type="text"
                              placeholder="e.g. TLB 849201 / WO-2026-441"
                              value={techLogRef}
                              onChange={(e) => setTechLogRef(e.target.value)}
                            />
                          </div>
                          <div className="submit-field" style={{ gridColumn: '1 / -1' }}>
                            <label>MEL / CDL Item Reference (if deferred)</label>
                            <input
                              type="text"
                              placeholder="e.g. MEL 29-11-01 Cat C or None"
                              value={melItemRef}
                              onChange={(e) => setMelItemRef(e.target.value)}
                            />
                          </div>
                        </div>
                      )}

                      {/* 4. GROUND_OPS */}
                      {selectedReportKind.id === 'GROUND_OPS' && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                          <div className="submit-field">
                            <label>Airport / Station</label>
                            <input
                              type="text"
                              placeholder="e.g. LTFM (Istanbul Airport)"
                              value={airportLocation}
                              onChange={(e) => setAirportLocation(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Apron Stand / Gate / Ramp Area</label>
                            <input
                              type="text"
                              placeholder="e.g. Stand 214, Gate B04"
                              value={apronGateStand}
                              onChange={(e) => setApronGateStand(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Flight Number Involved</label>
                            <input
                              type="text"
                              placeholder="e.g. TK 1702"
                              value={flightNumber}
                              onChange={(e) => setFlightNumber(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Aircraft Registration</label>
                            <input
                              type="text"
                              placeholder="e.g. TC-LSD"
                              value={aircraftReg}
                              onChange={(e) => setAircraftReg(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Ground Handling Company</label>
                            <select
                              value={groundHandlingAgent}
                              onChange={(e) => setGroundHandlingAgent(e.target.value)}
                              className="submit-select"
                            >
                              <option value="TGS Ground Services">TGS Ground Services</option>
                              <option value="Havaş Ground Handling">Havaş Ground Handling</option>
                              <option value="Çelebi Ground Handling">Çelebi Ground Handling</option>
                              <option value="Airline In-House Ramp">Airline In-House Ramp</option>
                              <option value="Foreign Station Handler">Foreign Station Handler</option>
                            </select>
                          </div>
                          <div className="submit-field">
                            <label>Ground Equipment Involved (GSE)</label>
                            <select
                              value={groundEquipmentType}
                              onChange={(e) => setGroundEquipmentType(e.target.value)}
                              className="submit-select"
                            >
                              <option value="Pushback Tug & Towbar">Pushback Tug & Towbar</option>
                              <option value="Belt Loader / Conveyor">Belt Loader / Conveyor</option>
                              <option value="Baggage Tractor & Carts">Baggage Tractor & Carts</option>
                              <option value="Passenger Boarding Bridge (PBB)">Passenger Boarding Bridge (PBB)</option>
                              <option value="Catering High-Loader Truck">Catering High-Loader Truck</option>
                              <option value="Ground Power Unit (GPU)">Ground Power Unit (GPU)</option>
                              <option value="Air Start Unit (ASU)">Air Start Unit (ASU)</option>
                              <option value="Refueling Tanker / Hydrant">Refueling Tanker / Hydrant</option>
                              <option value="De-icing Truck">De-icing Truck</option>
                              <option value="None / Personnel Action Only">None / Personnel Action Only</option>
                            </select>
                          </div>
                        </div>
                      )}

                      {/* 5. SECURITY */}
                      {selectedReportKind.id === 'SECURITY' && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                          <div className="submit-field">
                            <label>Location / Terminal / Gate</label>
                            <input
                              type="text"
                              placeholder="e.g. International Terminal Gate C3"
                              value={airportLocation}
                              onChange={(e) => setAirportLocation(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Security Domain</label>
                            <select
                              value={securityDomain}
                              onChange={(e) => setSecurityDomain(e.target.value)}
                              className="submit-select"
                            >
                              <option value="Airport Restricted Area / Access Control">Airport Restricted Area / Access Control</option>
                              <option value="Aircraft Security Search Discrepancy">Aircraft Security Search Discrepancy</option>
                              <option value="Baggage / Cargo Security Screening">Baggage / Cargo Security Screening</option>
                              <option value="Unruly / Disruptive Passenger (AVSEC)">Unruly / Disruptive Passenger (AVSEC)</option>
                              <option value="Cyber / IT Information Security">Cyber / IT Information Security</option>
                              <option value="Facility Perimeter / Hangar Breach">Facility Perimeter / Hangar Breach</option>
                            </select>
                          </div>
                          <div className="submit-field">
                            <label>Airport Police / Local Security Notified?</label>
                            <select
                              value={authoritiesNotified}
                              onChange={(e) => setAuthoritiesNotified(e.target.value)}
                              className="submit-select"
                            >
                              <option value="Yes">Yes - Police Dispatched</option>
                              <option value="No">No</option>
                              <option value="Not Required">Not Required</option>
                            </select>
                          </div>
                        </div>
                      )}

                      {/* 6. BIRD_STRIKE */}
                      {selectedReportKind.id === 'BIRD_STRIKE' && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                          <div className="submit-field">
                            <label>Flight Number</label>
                            <input
                              type="text"
                              placeholder="e.g. TK 2410"
                              value={flightNumber}
                              onChange={(e) => setFlightNumber(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Aircraft Registration</label>
                            <input
                              type="text"
                              placeholder="e.g. TC-JRO"
                              value={aircraftReg}
                              onChange={(e) => setAircraftReg(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Airport / Vicinity</label>
                            <input
                              type="text"
                              placeholder="e.g. LTFM (Istanbul)"
                              value={airportLocation}
                              onChange={(e) => setAirportLocation(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Runway Used</label>
                            <input
                              type="text"
                              placeholder="e.g. RWY 35L"
                              value={runwayUsed}
                              onChange={(e) => setRunwayUsed(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Flight Phase</label>
                            <select
                              value={flightPhase}
                              onChange={(e) => setFlightPhase(e.target.value)}
                              className="submit-select"
                            >
                              <option value="Takeoff Roll">Takeoff Roll</option>
                              <option value="Initial Climb">Initial Climb</option>
                              <option value="En-route / Cruise">En-route / Cruise</option>
                              <option value="Approach">Approach</option>
                              <option value="Landing Roll">Landing Roll</option>
                            </select>
                          </div>
                          <div className="submit-field">
                            <label>Aircraft Part Struck</label>
                            <select
                              value={partStruck}
                              onChange={(e) => setPartStruck(e.target.value)}
                              className="submit-select"
                            >
                              <option value="Radome / Nose">Radome / Nose</option>
                              <option value="Windshield">Windshield</option>
                              <option value="Engine 1 (Left)">Engine 1 (Left)</option>
                              <option value="Engine 2 (Right)">Engine 2 (Right)</option>
                              <option value="Wing / Leading Edge">Wing / Leading Edge</option>
                              <option value="Landing Gear">Landing Gear</option>
                              <option value="Fuselage">Fuselage</option>
                              <option value="Tail / Empennage">Tail / Empennage</option>
                            </select>
                          </div>
                          <div className="submit-field">
                            <label>Bird Species / Size</label>
                            <select
                              value={birdSpeciesSize}
                              onChange={(e) => setBirdSpeciesSize(e.target.value)}
                              className="submit-select"
                            >
                              <option value="Small (Starling, Sparrow)">Small (Starling, Sparrow)</option>
                              <option value="Medium (Gull, Pigeon, Hawk)">Medium (Gull, Pigeon, Hawk)</option>
                              <option value="Large (Goose, Stork, Eagle, Pelican)">Large (Goose, Stork, Eagle, Pelican)</option>
                              <option value="Unknown">Unknown</option>
                            </select>
                          </div>
                          <div className="submit-field">
                            <label>Number of Birds</label>
                            <select
                              value={birdCount}
                              onChange={(e) => setBirdCount(e.target.value)}
                              className="submit-select"
                            >
                              <option value="Single (1)">Single (1)</option>
                              <option value="Small Flock (2-10)">Small Flock (2-10)</option>
                              <option value="Large Flock (11+)">Large Flock (11+)</option>
                            </select>
                          </div>
                          <div className="submit-field">
                            <label>Pilot Warned of Birds by ATC?</label>
                            <select
                              value={pilotWarned}
                              onChange={(e) => setPilotWarned(e.target.value)}
                              className="submit-select"
                            >
                              <option value="No">No</option>
                              <option value="Yes (ATIS / ATC Advisory)">Yes (ATIS / ATC Advisory)</option>
                            </select>
                          </div>
                        </div>
                      )}

                      {/* 7. FATIGUE */}
                      {selectedReportKind.id === 'FATIGUE' && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                          <div className="submit-field">
                            <label>Duty Start Time (UTC)</label>
                            <input
                              type="time"
                              value={dutyStartTime}
                              onChange={(e) => setDutyStartTime(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Duty End Time (UTC)</label>
                            <input
                              type="time"
                              value={dutyEndTime}
                              onChange={(e) => setDutyEndTime(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Consecutive Duty Days Worked</label>
                            <input
                              type="number"
                              min={1}
                              max={14}
                              value={consecutiveDutyDays}
                              onChange={(e) => setConsecutiveDutyDays(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Sleep in Last 24 Hours (Hours)</label>
                            <input
                              type="number"
                              step="0.5"
                              value={sleepLast24}
                              onChange={(e) => setSleepLast24(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Sleep in Last 48 Hours (Hours)</label>
                            <input
                              type="number"
                              step="0.5"
                              value={sleepLast48}
                              onChange={(e) => setSleepLast48(e.target.value)}
                            />
                          </div>
                          <div className="submit-field" style={{ gridColumn: '1 / -1' }}>
                            <label>Samn-Perelli Fatigue Scale</label>
                            <select
                              value={fatigueRating}
                              onChange={(e) => setFatigueRating(e.target.value)}
                              className="submit-select"
                            >
                              <option value="1 - Fully alert, wide awake, extremely energetic">1 - Fully alert, wide awake, extremely energetic</option>
                              <option value="2 - Very lively, responsive, not at all tired">2 - Very lively, responsive, not at all tired</option>
                              <option value="3 - Okay, somewhat fresh">3 - Okay, somewhat fresh</option>
                              <option value="4 - A little tired, less than fresh">4 - A little tired, less than fresh</option>
                              <option value="5 - Moderately tired, let down">5 - Moderately tired, let down</option>
                              <option value="6 - Extremely tired, very difficult to concentrate">6 - Extremely tired, very difficult to concentrate</option>
                              <option value="7 - Completely exhausted, unable to function effectively">7 - Completely exhausted, unable to function effectively</option>
                            </select>
                          </div>
                        </div>
                      )}

                      {/* 8. FDP_EXTENSION */}
                      {selectedReportKind.id === 'FDP_EXTENSION' && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                          <div className="submit-field">
                            <label>Flight Number & Sector</label>
                            <input
                              type="text"
                              placeholder="e.g. TK 1983 IST-LHR"
                              value={flightNumber}
                              onChange={(e) => setFlightNumber(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Aircraft Registration</label>
                            <input
                              type="text"
                              placeholder="e.g. TC-JNL"
                              value={aircraftReg}
                              onChange={(e) => setAircraftReg(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Scheduled Duty End (UTC)</label>
                            <input
                              type="time"
                              value={scheduledDutyEnd}
                              onChange={(e) => setScheduledDutyEnd(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Actual Extended Duty End (UTC)</label>
                            <input
                              type="time"
                              value={actualDutyEnd}
                              onChange={(e) => setActualDutyEnd(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Extension Duration (Minutes)</label>
                            <input
                              type="number"
                              placeholder="e.g. 90"
                              value={extensionMinutes}
                              onChange={(e) => setExtensionMinutes(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Rest Facility Utilized</label>
                            <select
                              value={restFacilityUsed}
                              onChange={(e) => setRestFacilityUsed(e.target.value)}
                              className="submit-select"
                            >
                              <option value="Class 1 Rest Facility (Bunk)">Class 1 Rest Facility (Bunk)</option>
                              <option value="Class 2 (Lie-flat seat)">Class 2 (Lie-flat seat)</option>
                              <option value="Class 3 (Reclining seat)">Class 3 (Reclining seat)</option>
                              <option value="None">None</option>
                            </select>
                          </div>
                          <div className="submit-field" style={{ gridColumn: '1 / -1' }}>
                            <label>Reason for Commander Discretion / Extension</label>
                            <input
                              type="text"
                              placeholder="e.g. En-route severe convective weather reroute + ATC slot congestion"
                              value={discretionReason}
                              onChange={(e) => setDiscretionReason(e.target.value)}
                            />
                          </div>
                        </div>
                      )}

                      {/* 9. FACILITIES */}
                      {selectedReportKind.id === 'FACILITIES' && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                          <div className="submit-field">
                            <label>Facility Name / Building</label>
                            <input
                              type="text"
                              placeholder="e.g. Main Technical Hangar / Tool Store"
                              value={facilityName}
                              onChange={(e) => setFacilityName(e.target.value)}
                            />
                          </div>
                          <div className="submit-field">
                            <label>Room / Bay / Rack Reference</label>
                            <input
                              type="text"
                              placeholder="e.g. Bay 2, Chemical Cabinet 4"
                              value={roomBayRef}
                              onChange={(e) => setRoomBayRef(e.target.value)}
                            />
                          </div>
                          <div className="submit-field" style={{ gridColumn: '1 / -1' }}>
                            <label>Facility Discrepancy Type</label>
                            <select
                              value={facilityDiscrepancyType}
                              onChange={(e) => setFacilityDiscrepancyType(e.target.value)}
                              className="submit-select"
                            >
                              <option value="Fire Safety / Extinguisher Inspection">Fire Safety / Extinguisher Inspection</option>
                              <option value="Chemical Spill / Hazardous Waste Handling">Chemical Spill / Hazardous Waste Handling</option>
                              <option value="Apron / Hangar Lighting & Electrical Discrepancy">Apron / Hangar Lighting & Electrical Discrepancy</option>
                              <option value="FOD Hazard / Surface Discrepancy">FOD Hazard / Surface Discrepancy</option>
                              <option value="Equipment / Tooling Calibration Discrepancy">Equipment / Tooling Calibration Discrepancy</option>
                              <option value="Building / Infrastructure Structural Defect">Building / Infrastructure Structural Defect</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Step 2 Actions */}
                <div className="submit-step-footer">
                  <button
                    type="button"
                    className="submit-btn-back"
                    onClick={() => setCurrentStep(1)}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="submit-btn-next"
                    onClick={handleNextFromStep2}
                  >
                    <span>Next</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3: OCCURRENCE INFORMATION */}
            {currentStep === 3 && (
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <div style={{ marginBottom: '14px' }}>
                  <h4 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', margin: '0 0 4px 0' }}>
                    Occurrence Information
                  </h4>
                  <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                    Provide comprehensive factual details regarding what occurred and any immediate mitigations taken.
                  </p>
                </div>

                <div className="submit-form-scroll-wrap">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Field 1: What Happened? */}
                    <div className="submit-field">
                      <label htmlFor="what-happened" style={{ fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>
                        What Happened?
                      </label>
                      <textarea
                        id="what-happened"
                        rows={4}
                        placeholder="Describe what happened in detail..."
                        value={whatHappened}
                        onChange={(e) => setWhatHappened(e.target.value)}
                        autoFocus
                      />
                    </div>

                    {/* Field 2: Why Happened? (Screenshot 4 & 5) */}
                    <div className="submit-field">
                      <label htmlFor="why-happened" style={{ fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>
                        Why Happened?
                      </label>
                      <textarea
                        id="why-happened"
                        rows={4}
                        placeholder="Describe why it happened or contributing factors..."
                        value={whyHappened}
                        onChange={(e) => setWhyHappened(e.target.value)}
                      />
                    </div>

                    {/* Field 3: Corrective Action & Related Procedure? (Screenshot 5) */}
                    <div className="submit-field">
                      <label htmlFor="corrective-action" style={{ fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>
                        Corrective Action & Related Procedure?
                      </label>
                      <textarea
                        id="corrective-action"
                        rows={4}
                        placeholder="Specify corrective action taken and related procedure..."
                        value={correctiveAction}
                        onChange={(e) => setCorrectiveAction(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Step 3 Actions */}
                <div className="submit-step-footer">
                  <button
                    type="button"
                    className="submit-btn-back"
                    onClick={() => setCurrentStep(2)}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="submit-btn-next"
                    onClick={handleNextFromStep3}
                  >
                    <span>Next</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 4: ATTACHMENT */}
            {currentStep === 4 && (
              <form onSubmit={handleFinalSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <div style={{ marginBottom: '14px' }}>
                  <h4 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', margin: '0 0 4px 0' }}>
                    Attachments & Evidence
                  </h4>
                  <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                    Attach supporting documentation, photographs, flight recorder dumps, or tech log sheets.
                  </p>
                </div>

                <div className="submit-form-scroll-wrap">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Drag and drop upload zone */}
                    <label
                      htmlFor="submit-file-input"
                      style={{
                        border: '2px dashed #cbd5e1',
                        borderRadius: '12px',
                        padding: '24px',
                        textAlign: 'center',
                        backgroundColor: '#f8fafc',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        display: 'block'
                      }}
                      className="submit-dropzone"
                    >
                      <input
                        id="submit-file-input"
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                      <div
                        style={{
                          width: '44px',
                          height: '44px',
                          borderRadius: '50%',
                          backgroundColor: '#eff6ff',
                          color: '#2563eb',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: '10px'
                        }}
                      >
                        <UploadCloud size={22} />
                      </div>
                      <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#0f172a' }}>
                        Click to browse or drag and drop files here
                      </div>
                      <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '4px' }}>
                        PDF, JPG, PNG, DOCX, CSV or XLSX (Max 25MB per file)
                      </div>
                    </label>

                    {/* Attached files list */}
                    {attachments.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>
                          Attached Files ({attachments.length}):
                        </span>
                        {attachments.map((file, idx) => (
                          <div
                            key={idx}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '8px 12px',
                              backgroundColor: '#f1f5f9',
                              borderRadius: '8px',
                              fontSize: '12.5px'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <FileText size={15} color="#2563eb" />
                              <span style={{ fontWeight: 600, color: '#0f172a' }}>{file.name}</span>
                              <span style={{ fontSize: '11px', color: '#64748b' }}>({file.size})</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveFile(idx)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#94a3b8',
                                cursor: 'pointer',
                                padding: '4px'
                              }}
                              title="Remove file"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Additional Notes */}
                    <div className="submit-field">
                      <label htmlFor="rep-notes" style={{ fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>
                        Additional Remarks or Witness Information
                      </label>
                      <textarea
                        id="rep-notes"
                        rows={3}
                        placeholder="Any additional witnesses, contact numbers, or immediate containment notes..."
                        value={reporterNotes}
                        onChange={(e) => setReporterNotes(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Step 4 Actions */}
                <div className="submit-step-footer">
                  <button
                    type="button"
                    className="submit-btn-back"
                    onClick={() => setCurrentStep(3)}
                    disabled={isSubmitting}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="submit-btn-next"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="spin-animate" style={{ width: '14px', height: '14px', border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%' }} />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <Send size={14} />
                        <span>Submit Report</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
